// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	ASSISTANT_RESPONSE_FIELD_PREFIX,
	ASSISTANT_RESPONSE_PROVIDER,
	decodeAssistantResponseFields,
} from '@fluxer/schema/src/domains/konvo/AssistantResponse';
import {z} from 'zod';

/**
 * KONVO: the tidy Assistant chat (design/assistant-dm-cleanup). The server keeps, per person, which
 * messages belong to which conversation with Konvo, which cards they archived or pinned, which kinds
 * of update they muted, the one live Agents card and its history. The client folds the chat with it.
 */

/** What a card in the Assistant chat is about. Mute works per kind; History lists one kind. */
export const ASSISTANT_CHAT_KINDS = [
	'agents',
	'agent_usage',
	'brief',
	'agenda',
	'calendar',
	'reminder',
	'digest',
	'time',
	'monday',
	'nudge',
	'feedback',
	'connect',
	'action',
	'dashboard',
	'other',
] as const;
export type AssistantChatKind = (typeof ASSISTANT_CHAT_KINDS)[number];
export const AssistantChatKindSchema = z.enum(ASSISTANT_CHAT_KINDS);

/** Kinds Konvo posts by itself (not as part of a reply), which can be muted. */
export const ASSISTANT_CHAT_MUTABLE_KINDS: ReadonlyArray<AssistantChatKind> = [
	'agents',
	'agent_usage',
	'brief',
	'agenda',
	'calendar',
	'reminder',
	'digest',
	'time',
	'monday',
	'nudge',
	'feedback',
];

export const ASSISTANT_ACTION_PREVIEW_SOURCE = 'Konvo · Action preview';

interface EmbedLike {
	provider?: {name?: string | null} | null;
	author?: {name?: string | null} | null;
	fields?: ReadonlyArray<{name?: string | null; value?: string | null}> | null;
}

/** The kind of a system card from its embed: provider `Konvo:<kind>`, block type for assistant responses. */
export function assistantChatKindOfEmbed(embed: EmbedLike): AssistantChatKind | null {
	const provider = embed.provider?.name ?? '';
	if (!provider.startsWith('Konvo:')) return null;
	if (provider === ASSISTANT_RESPONSE_PROVIDER) {
		const hasBlocks = (embed.fields ?? []).some((field) => field.name?.startsWith(ASSISTANT_RESPONSE_FIELD_PREFIX));
		if (!hasBlocks) return 'other';
		const response = decodeAssistantResponseFields(embed.fields);
		const types = new Set(response?.blocks.map((block) => block.type) ?? []);
		if (types.has('agents')) return 'agents';
		if (types.has('agent_usage')) return 'agent_usage';
		// The agents digest is a digest: muted with the other digests, not with the live Agents card.
		if (types.has('agents_digest')) return 'digest';
		// The end-of-day wrap-up is something Konvo brought up by itself: muted with the briefs.
		if (types.has('brief') || types.has('wrap_up')) return 'brief';
		if (types.has('dashboard')) return 'dashboard';
		return 'other';
	}
	const name = provider.slice('Konvo:'.length);
	// Action previews and automation approvals share the agenda look (KonvoAssistantActionCard).
	if (name === 'agenda' && embed.author?.name === ASSISTANT_ACTION_PREVIEW_SOURCE) return 'action';
	return (ASSISTANT_CHAT_KINDS as ReadonlyArray<string>).includes(name) ? (name as AssistantChatKind) : 'other';
}

/** A conversation with Konvo: the person's messages and Konvo's replies (text and cards) of its turns. */
export interface AssistantChatConversation {
	id: string;
	title: string;
	started_at: string;
	updated_at: string;
	/** Oldest first; the person's messages and Konvo's replies, never unprompted updates. */
	message_ids: Array<string>;
	archived: boolean;
}

/** One line in a card's History, newest first in the response. */
export interface AssistantChatHistoryEvent {
	at: string;
	kind: AssistantChatKind;
	title: string;
	detail?: string;
	tone: 'needs' | 'running' | 'done' | 'failed' | 'info';
	machine_id?: string;
	thread_id?: string;
}

/** GET /konvo/assistant/chat */
export interface AssistantChatState {
	/** Newest first, up to 100, from the last 90 days. */
	conversations: Array<AssistantChatConversation>;
	archived_message_ids: Array<string>;
	pinned_message_ids: Array<string>;
	muted_kinds: Array<AssistantChatKind>;
	/** The message that currently carries each live card (only Agents for now). */
	live: {agents?: string};
}

/** GET /konvo/assistant/chat/history?kind=agents */
export interface AssistantChatHistoryResponse {
	kind: AssistantChatKind;
	events: Array<AssistantChatHistoryEvent>;
}

const SnowflakeString = z.string().regex(/^\d{1,20}$/);

/** PUT /konvo/assistant/chat/archive: archive (or bring back) cards and whole conversations. */
export const AssistantChatArchiveRequest = z.object({
	message_ids: z.array(SnowflakeString).max(500).default([]),
	conversation_ids: z.array(SnowflakeString).max(100).default([]),
	archived: z.boolean(),
});

/** PUT /konvo/assistant/chat/pin */
export const AssistantChatPinRequest = z.object({
	message_id: SnowflakeString,
	pinned: z.boolean(),
});

/** PUT /konvo/assistant/chat/mute: no push for this kind; live cards update quietly in place. */
export const AssistantChatMuteRequest = z.object({
	kind: AssistantChatKindSchema,
	muted: z.boolean(),
});

/** PATCH /konvo/assistant/chat/conversations/:id */
export const AssistantChatRenameRequest = z.object({
	title: z.string().trim().min(1).max(80),
});

export const ASSISTANT_CHAT_MAX_PINNED = 10;
export const ASSISTANT_CHAT_MAX_ARCHIVED = 3000;
export const ASSISTANT_CHAT_HISTORY_KEEP = 200;

/** A conversation's title from its first message: the first line, without filler, at most 60 characters; '' when it has no text. */
export function assistantConversationTitle(text: string): string {
	const line =
		text
			.replace(/<[@#:][^>]*>/g, '')
			.split('\n')
			.map((part) => part.trim())
			.find(Boolean) ?? '';
	const cleaned = line
		.replace(/^(hey|hi|hello|ok|okay|so|please)\b[,!.\s]*/i, '')
		.replace(/^(konvo|@konvo assistant)\b[,:\s]*/i, '')
		.replace(/\s+/g, ' ')
		.trim();
	const base = cleaned || line;
	// No words to go on (only a picture, say): the app shows its own "Conversation" label.
	if (!base) return '';
	const capped = base.length > 60 ? `${base.slice(0, 57).replace(/\s+\S*$/, '')}…` : base;
	return capped.charAt(0).toUpperCase() + capped.slice(1);
}
